import React from 'react';
import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import type { StorageUsage } from '../../lib/cfos/types';

interface StorageMeterProps {
  usage: StorageUsage | null | undefined;
  compact?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const StorageMeter: React.FC<StorageMeterProps> = ({ usage, compact }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sand = '#ccba9b';

  if (!usage) {
    return (
      <Typography variant="caption" color="text.secondary">
        Storage unavailable
      </Typography>
    );
  }

  const pct = usage.quota_bytes > 0
    ? Math.min(100, Math.round((usage.used_bytes / usage.quota_bytes) * 100))
    : 0;

  return (
    <Box sx={{ minWidth: compact ? 120 : 200 }}>
      {!compact && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Storage
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatBytes(usage.used_bytes)} / {formatBytes(usage.quota_bytes)}
          </Typography>
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: compact ? 6 : 8,
          borderRadius: 1,
          bgcolor: isDark ? 'rgba(204, 186, 155, 0.1)' : 'rgba(15, 33, 52, 0.08)',
          '& .MuiLinearProgress-bar': {
            bgcolor: pct > 90 ? 'error.main' : sand,
            borderRadius: 1,
          },
        }}
      />
      {!compact && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {usage.file_count} file{usage.file_count === 1 ? '' : 's'} · {pct}% used
        </Typography>
      )}
    </Box>
  );
};

export default StorageMeter;
